import React from "react";
import * as Icons from "lucide-react";
import { FlaskConical, CheckCircle2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import SectionHeading from "../components/SectionHeading";
import AppointmentCTA from "../components/AppointmentCTA";
import FAQSection from "../components/FAQSection";
import { investigationsList, investigationsFaqs, clinic } from "../data/mock";

const Investigations = () => {
  return (
    <div>
      <SEO path="/liver-tests-varanasi" faqs={investigationsFaqs} />
      <PageHero breadcrumb="Liver Investigations"
        title="Liver Investigations & Diagnostic Tests"
        subtitle="Advanced, accurate liver testing — FibroScan, LFT, SGPT/SGOT, ultrasound, CT/MRI and biopsy — in Varanasi." />

      <section className="py-16 sm:py-20">
        <div className="container-app max-w-4xl text-center">
          <SectionHeading eyebrow="Why It Matters" title="Why Liver Tests Are Important" />
          <p className="text-slate-600 leading-relaxed text-lg">
            Liver diseases often develop silently, with no symptoms until significant damage has occurred. Timely investigations detect problems early — when they are most treatable and often reversible. Accurate testing also guides the right treatment and helps track your recovery over time.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <SectionHeading eyebrow="Diagnostics" title="Liver Tests We Offer" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {investigationsList.map((d, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 text-white grid place-items-center shadow-lg shadow-sky-500/30"><FlaskConical size={22} /></div>
                  <span className="text-xs font-semibold text-sky-600 bg-sky-50 px-3 py-1 rounded-full">{d.tag}</span>
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{d.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-app">
          <div className="bg-sky-50 border border-sky-100 rounded-3xl p-8 sm:p-12 max-w-4xl mx-auto">
            <SectionHeading center={false} eyebrow="Guidance" title="Which Test Do You Need?" />
            <ul className="space-y-4">
              {[
                "Abnormal LFT or high SGPT/SGOT report → FibroScan + Ultrasound",
                "Suspected fatty liver → Ultrasound + FibroScan",
                "Jaundice or high bilirubin → LFT + Ultrasound + viral markers",
                "Chronic liver disease follow-up → FibroScan + LFT monitoring",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 size={20} className="text-sky-500 mt-0.5 shrink-0" />
                  <span className="text-sm sm:text-base">{t}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-slate-600 text-sm">Not sure which test is right for you? {clinic.doctorName} will recommend the exact investigations you need based on your symptoms and reports.</p>
            <Link to="/contact" className="inline-flex items-center gap-1 text-sky-600 font-semibold mt-4 hover:gap-2 transition-all">Book a consultation <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      <FAQSection faqs={investigationsFaqs} title="Liver Investigations — FAQs" />
      <AppointmentCTA title="Get Your Liver Checked Accurately" />
    </div>
  );
};

export default Investigations;
